import { Box, ChevronDownIcon, ChevronUpIcon, Container, Flex, Grid, Loader } from "@eworkit/iris";
import React, { useEffect, useRef, useState } from "react";
import { FC } from "react";

import { InfiniteResultState } from "@/core/queries/useInfiniteQuery";
import { JobRequestListDto } from "@/data/job-requests/types/JobRequest";
import { useInfiniteScroll } from "@/hooks/InfinityScroll";

import { DashboardJobsGridItem } from "./DashboardJobsGridItem";
import { STANDARD_VISIBLE_HEIGHT } from "./DashboardJobsGridRenderer";

interface DashboardJobsExpandableGridProps {
  query: InfiniteResultState<JobRequestListDto, unknown>;
}

export const DashboardJobsExpandableGrid: FC<DashboardJobsExpandableGridProps> = ({
  query: { data, isFetching, hasNextPage, fetchNextPage },
}) => {
  const [expanded, setExpanded] = useState(false);
  const [gridHeight, setGridHeight] = useState(STANDARD_VISIBLE_HEIGHT);
  const gridRef = useRef<HTMLDivElement>(null);

  const infiniteRef = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: Boolean(hasNextPage),
    onLoadMore: fetchNextPage,
  });

  useEffect(() => {
    setGridHeight(expanded ? gridRef.current?.clientHeight ?? 0 : STANDARD_VISIBLE_HEIGHT);
  }, [expanded, gridRef, data]);

  const ChevronIcon = expanded ? ChevronUpIcon : ChevronDownIcon;

  return (
    <Container>
      <Container
        ref={expanded ? infiniteRef : undefined}
        sx={{
          height: `${gridHeight}px`,
          transition: "height 0.5s",
          position: "relative",
          overflow: "hidden",
        }}>
        <Grid ref={gridRef} columns={["repeat(2, 1fr)", null, null, "repeat(4, 1fr)"]} gap={["xs", null, null, "sm"]}>
          {data?.pages.map(page => page.content.map(item => <DashboardJobsGridItem key={item.id} item={item} />))}
        </Grid>
      </Container>
      {expanded && isFetching && (
        <Flex alignItems="center" justifyContent="center" my="md">
          <Loader size="xl" />
        </Flex>
      )}
      <Container>
        <Container
          as={Box}
          justifySelf="center"
          pt={expanded && "sm"}
          sx={{ textAlign: "center", position: "relative" }}>
          {!expanded && (
            <Container
              sx={{
                height: "80px",
                position: "absolute",
                width: "100%",
                top: "-80px",
                backgroundImage: "linear-gradient(to bottom, transparent, #f0f2f4)",
              }}></Container>
          )}
          <ChevronIcon
            sx={{ "&:hover": { color: "vg500", cursor: "pointer" } }}
            size="xl"
            color="n700"
            onClick={() => setExpanded(!expanded)}
          />
        </Container>
      </Container>
    </Container>
  );
};
